/**
 * SalesRepTaskInbox — COA-10 G1
 * ==============================
 * Sales-rep-side consumption surface for case-plan rows where
 * `default_role = sales_rep`. Read-only inbox. Rendered inside
 * SalesRepDashboard so it sits next to the lead pipeline without
 * disturbing the existing lead-funnel UX.
 *
 * Data: GET /api/clinic/sales-rep/tasks (COA-10).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClipboardList, AlertOctagon, Clock, CheckCircle2, Circle, RefreshCw } from 'lucide-react';
import { api } from '../../utils/api';

const PILL = (tone) => {
  const map = {
    rose:    'bg-rose-100 text-rose-700 ring-rose-200',
    amber:   'bg-amber-100 text-amber-700 ring-amber-200',
    emerald: 'bg-emerald-100 text-emerald-700 ring-emerald-200',
    slate:   'bg-slate-100 text-slate-600 ring-slate-200',
    sky:     'bg-sky-100 text-sky-700 ring-sky-200',
  };
  return `inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold ring-1 ${map[tone] || map.slate}`;
};

const FILTERS = [
  { id: 'all',      labelKey: 'provider.salesRepTasks.filterAll' },
  { id: 'mine',     labelKey: 'provider.salesRepTasks.filterMine' },
  { id: 'pool',     labelKey: 'provider.salesRepTasks.filterPool' },
  { id: 'overdue',  labelKey: 'provider.salesRepTasks.filterOverdue' },
];

export default function SalesRepTaskInbox() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [people, setPeople] = useState({});
  const [buckets, setBuckets] = useState({ all: 0, mine: 0, pool: 0, overdue: 0, satisfied: 0 });
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/clinic/sales-rep/tasks');
      if (data?.success) {
        setItems(Array.isArray(data.items) ? data.items : []);
        setBuckets(data.buckets || { all: 0, mine: 0, pool: 0, overdue: 0, satisfied: 0 });
        setPeople(data.people || {});
      }
    } catch (e) {
      setError('Could not load coordinator tasks');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const visible = useMemo(() => {
    if (filter === 'mine')    return items.filter((x) => x.is_mine);
    if (filter === 'pool')    return items.filter((x) => x.is_unassigned);
    if (filter === 'overdue') return items.filter((x) => x.is_overdue);
    return items;
  }, [items, filter]);

  const friendlyAssignee = useCallback((id) => {
    if (!id) return null;
    if (String(id) === 'SYSTEM_POOL') return 'Sales Pool';
    return people[String(id)]?.display_name || `#${String(id).slice(0, 8)}`;
  }, [people]);

  return (
    <div
      className="bg-white border border-slate-200 rounded-xl shadow-sm mb-6"
      data-testid="salesrep-task-inbox"
    >
      <header className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <ClipboardList className="h-4 w-4 text-slate-500 shrink-0" />
          <div>
            <h2 className="text-sm font-semibold text-slate-900" data-testid="salesrep-task-title">
              {t('provider.salesRepTasks.inboxTitle')}
            </h2>
            <p className="text-[11px] text-slate-500">
              {t('provider.salesRepTasks.inboxSubtitle')}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={fetchTasks}
          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded border border-slate-200 hover:bg-slate-50 text-slate-700"
          data-testid="salesrep-task-refresh"
        >
          <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
          {t('provider.salesRepTasks.refresh')}
        </button>
      </header>

      {/* Bucket chips */}
      <div className="px-5 py-2 border-b border-slate-100 flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            data-testid={`salesrep-task-filter-${f.id}`}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ring-1 transition-colors ${
              filter === f.id
                ? 'bg-slate-900 text-white ring-slate-900'
                : 'bg-white text-slate-700 ring-slate-200 hover:ring-slate-300'
            }`}
          >
            {t(f.labelKey)}
            <span className={filter === f.id ? 'text-white/80' : 'text-slate-500'}>
              {buckets[f.id] ?? 0}
            </span>
          </button>
        ))}
        <span className="ml-auto text-[11px] text-slate-500">
          {t('provider.salesRepTasks.ofTasks', { shown: visible.length, total: items.length })}
        </span>
      </div>

      {/* List */}
      <div className="divide-y divide-slate-100">
        {loading && (
          <div className="px-5 py-6 text-center text-[12px] text-slate-500">
            {t('provider.salesRepTasks.loadingTasks')}
          </div>
        )}
        {!loading && error && (
          <div className="px-5 py-6 text-center text-[12px] text-rose-600" data-testid="salesrep-task-error">
            {error}
          </div>
        )}
        {!loading && !error && visible.length === 0 && (
          <div className="px-5 py-6 text-center" data-testid="salesrep-task-empty">
            <CheckCircle2 className="h-5 w-5 text-emerald-500 mx-auto mb-1.5" />
            <div className="text-[12px] font-medium text-slate-700">
              {t('provider.salesRepTasks.emptyTitle')}
            </div>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {t('provider.salesRepTasks.emptyHelp')}
            </p>
          </div>
        )}
        {!loading && !error && visible.map((task) => (
          <a
            key={`${task.case_id}::${task.row_id}`}
            href={`/#/clinic/cases/${task.case_id}/workflow`}
            data-testid={`salesrep-task-${task.row_id}`}
            className="block px-5 py-2.5 hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-start gap-3">
              <div className="shrink-0 mt-0.5">
                {task.is_satisfied ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                ) : task.is_overdue ? (
                  <AlertOctagon className="h-4 w-4 text-rose-500" />
                ) : (
                  <Circle className="h-4 w-4 text-slate-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[13px] font-semibold text-slate-900 truncate">
                    {task.task_name}
                  </span>
                  {task.is_escalated && (
                    <span className={PILL('rose')}>{t('provider.salesRepTasks.badgeEscalated')}</span>
                  )}
                  <span className={PILL(task.is_satisfied ? 'emerald' : task.is_overdue ? 'rose' : 'amber')}>
                    {task.is_satisfied ? t('provider.salesRepTasks.badgeSatisfied') : task.is_overdue ? t('provider.salesRepTasks.badgeOverdue') : t('provider.salesRepTasks.badgePending')}
                  </span>
                </div>
                <div className="mt-0.5 text-[11.5px] text-slate-600 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                  <span className="truncate">
                    <span className="text-slate-400">{t('provider.salesRepTasks.patientPrefix')} </span>
                    {task.patient_name || '—'}
                  </span>
                  <span className="truncate">
                    <span className="text-slate-400">{t('provider.salesRepTasks.casePrefix')} </span>
                    <span className="font-mono">#{(task.case_id || '').slice(-6)}</span>
                  </span>
                  <span className="truncate">
                    <span className="text-slate-400">{t('provider.salesRepTasks.treatmentPrefix')} </span>
                    {task.treatment_name || '—'}
                  </span>
                  <span className="truncate">
                    <span className="text-slate-400">{t('provider.salesRepTasks.assigneePrefix')} </span>
                    {task.is_mine ? (
                      <span className="text-sky-700 font-semibold">{t('provider.salesRepTasks.youLabel')}</span>
                    ) : (
                      friendlyAssignee(task.default_assignee) || t('provider.salesRepTasks.salesPool')
                    )}
                  </span>
                  {task.due_at && (
                    <span className="inline-flex items-center gap-1">
                      <Clock className="h-3 w-3 text-slate-400" />
                      <span className={task.is_overdue ? 'text-rose-600 font-semibold' : 'text-slate-600'}>
                        {t('provider.salesRepTasks.duePrefix', { date: String(task.due_at).slice(0, 10) })}
                      </span>
                    </span>
                  )}
                </div>
              </div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}
